import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckThinIcon, CopyIcon, TextInput } from '@natter-co/design-system';
import { useCopyToClipboard } from 'common/hooks/useCopyToClipboard';
import { useTimeout } from 'common/hooks/useTimeout';

import type { TextInputPosition } from '@natter-co/design-system';

const FEEDBACK_MS = 1_500;

type Status = 'idle' | 'copied' | 'failed';

type DsCopyFieldProps = {
  value: string;
  label: string;
  labelHidden?: boolean;
  position?: TextInputPosition;
};

/**
 * The Design System 2.0 read-only copy field. The trailing button swaps to a tick for a moment, and
 * a polite live region says what happened, because the swap alone is invisible to a screen reader.
 */
export const DsCopyField = ({ value, label, labelHidden, position }: DsCopyFieldProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'clipboard' });
  const [status, setStatus] = useState<Status>('idle');

  const { reset } = useTimeout(() => setStatus('idle'), FEEDBACK_MS);

  const { copyToClipboard } = useCopyToClipboard({
    onSuccess: () => {
      setStatus('copied');
      reset();
    },
    onError: () => {
      setStatus('failed');
      reset();
    },
  });

  const copied = status === 'copied';

  const handleCopy = () => {
    copyToClipboard({ text: value });
  };

  return (
    <>
      <TextInput
        label={label}
        labelHidden={labelHidden}
        position={position}
        readOnly
        value={value}
        rightButton={copied ? <CheckThinIcon /> : <CopyIcon />}
        rightButtonLabel={copied ? t('copiedToClipboard') : t('copy', { content: label })}
        onRightButtonClick={handleCopy}
      />
      <span role="status" className="sr-only">
        {copied && t('copiedToClipboard')}
        {status === 'failed' && t('copyToClipboardFailed')}
      </span>
    </>
  );
};
